import { useCallback, useMemo, useState } from "react";

export type FieldErrors<K extends string> = Partial<Record<K, string>>;

/** Regra de um campo: devolve a mensagem de erro ou `null` quando o valor é válido. */
export type FieldRules<K extends string> = Partial<Record<K, (value: unknown) => string | null>>;

export interface UseFieldErrorsResult<K extends string> {
  errors: FieldErrors<K>;
  setError: (key: K, message: string) => void;
  clearError: (key: K) => void;
  clearAll: () => void;
  hasErrors: boolean;
  /** Roda as regras sobre `values`, substitui os erros atuais pelos encontrados e devolve `true` se não há erro. */
  validate: (rules: FieldRules<K>, values: Partial<Record<K, unknown>>) => boolean;
}

/** Mensagens de erro por campo, para validação em tela antes de qualquer requisição. */
export function useFieldErrors<K extends string>(): UseFieldErrorsResult<K> {
  const [errors, setErrors] = useState<FieldErrors<K>>({});

  const setError = useCallback((key: K, message: string) => {
    setErrors((current) => ({ ...current, [key]: message }));
  }, []);

  const clearError = useCallback((key: K) => {
    setErrors((current) => {
      if (!(key in current)) return current;
      const { [key]: _removed, ...rest } = current;
      return rest as FieldErrors<K>;
    });
  }, []);

  const clearAll = useCallback(() => setErrors({}), []);

  const validate = useCallback((rules: FieldRules<K>, values: Partial<Record<K, unknown>>) => {
    const found: FieldErrors<K> = {};
    for (const key of Object.keys(rules) as K[]) {
      const message = rules[key]?.(values[key]) ?? null;
      if (message) found[key] = message;
    }
    setErrors(found);
    return Object.keys(found).length === 0;
  }, []);

  return useMemo(
    () => ({
      errors,
      setError,
      clearError,
      clearAll,
      hasErrors: Object.keys(errors).length > 0,
      validate,
    }),
    [errors, setError, clearError, clearAll, validate],
  );
}
